import { type HTMLAttributes, type ReactElement, useDeferredValue, useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled from 'types/theme.types';

export const Panel = styled.div`
  width: var(--navbar-width);
  height: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

export const PanelSection = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5em;

  overflow-y: auto;
  scrollbar-color: ${({ theme }) => theme.sideMenuText} rgba(0, 0, 0, 0);

  ::-webkit-scrollbar {
    width: 9px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sideMenuText};
    border-radius: 10px;
  }
`;

export type ItemProps = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  noHover?: boolean;
};

export const BaseItemContainer = styled.div`
  justify-content: flex-start;
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 15px;
  gap: 12px;
  min-width: 0;
`;

export const Item = styled(BaseItemContainer)<ItemProps>`
  height: 32px;
  cursor: pointer;
  color: ${({ theme }) => theme.navbarText};
  font-size: 13px;
  border-radius: 4px;

  /* Make SVG icons stroke-only with text color */
  svg {
    color: ${({ theme }) => theme.navbarText};
    stroke: ${({ theme }) => theme.navbarText};
    stroke-width: 0.7px;

    * {
      stroke: currentColor !important;
      stroke-width: inherit;
    }
  }

  ${({ active, theme }) =>
    active &&
    `
      background-color: ${theme.tertiaryGray};
      font-weight: bold;
      border-left: 3px solid ${theme.limeYellow};
      border-radius: 0;
      padding-left: 12px;
    `}

  ${({ noHover, theme }) =>
    !noHover &&
    `
      &:hover {
        background-color: ${theme.tertiaryGray};
        font-weigh: 500;
      }
    `}
`;

type ParentHeightProps = {
  bypass?: boolean;
  fallbackHeight?: number;
  children: (availableHeight?: number) => ReactElement;
};

export const ParentHeight = ({ bypass, fallbackHeight, children }: ParentHeightProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(fallbackHeight);
  const deferredHeight = useDeferredValue(availableHeight);

  useLayoutEffect(() => {
    const parent = ref.current?.parentElement;
    if (parent && !bypass) {
      const handleResize = () => setAvailableHeight(parent.clientHeight || fallbackHeight);
      handleResize();
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(parent);
      return () => resizeObserver.disconnect();
    }
  }, [bypass, fallbackHeight]);

  return bypass ? children() : <ParentHeightWrapper ref={ref}>{children(deferredHeight)}</ParentHeightWrapper>;
};

const ParentHeightWrapper = styled.div`
  max-height: 100%;
  overflow: hidden;
`;

export const ItemText = styled.span`
  font-size: 14px;
  vertical-align: middle;
  display: inline-block;
`;
